/**
 * Entity Form Generator (DRR-045)
 *
 * Generates forms for domain module entities (JournalEntry, GlAccount, Patient, etc.)
 * using context-aware UI schemas from the backend.
 *
 * Key differences from DynamicFormGenerator:
 * - Uses useEntityUISchema instead of useWorkflowMetadata
 * - Designed for domain module entities (subgraph types), not primitives
 * - Context-aware: Mode × Role × SafetyLevel determines field rendering
 *
 * Architecture (DRR-045):
 * - Backend controls ALL rendering decisions via FormSchema
 * - Frontend is dumb - it renders what backend says
 * - Widget registry is component-only (no metadata)
 */

import { Alert, Box, Button, Collapse, Grid, Group, Loader, Paper, Stack, Stepper, Tabs, Text, UnstyledButton } from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconChevronRight, IconShield } from '@tabler/icons-react';
import React, { Suspense, useCallback, useMemo, useState } from 'react';

import {
  useEntityUISchema,
  SUPPORTED_SCHEMA_VERSION,
  type UIContext,
  type UISchemaField,
} from '../hooks/useEntityUISchema';
import { getWidgetComponent, hasWidgetComponent } from '../widgets/registry';
import type { BaseWidgetProps, ComplianceContext } from '../widgets/types';
import { EntitySearchField } from './EntitySearchField';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Props for custom renderer components provided by subgraphs.
 * Custom renderers receive the same context as standard fields
 * but can render arbitrary domain-specific UI (e.g., FHIR timeline).
 */
export interface CustomRendererProps {
  /** Field definition from the UI schema */
  field: UISchemaField;
  /** Current field value */
  value: unknown;
  /** Change handler */
  onChange: (value: unknown) => void;
  /** All current form values for cross-field references */
  formValues: Record<string, unknown>;
  /** Compliance context for life-critical rendering */
  complianceContext?: ComplianceContext;
  /** Whether the field is disabled */
  disabled?: boolean;
}

export interface EntityFormGeneratorProps {
  /** Subgraph name (e.g., 'accounting', 'fhir') */
  subgraph: string;
  /** Entity type name (e.g., 'JournalEntry', 'Patient') */
  entityType: string;
  /** DRR-162: Optional RuleSet ID for service-line-specific form override resolution.
   * Pass from constellation instance's primary_ruleset. */
  rulesetId?: string;
  /** UI context for schema resolution */
  context: UIContext;
  /** Initial form values (for edit mode) */
  initialValues?: Record<string, unknown>;
  /** Called when form is submitted */
  onSubmit?: (values: Record<string, unknown>) => void;
  /** Called when any field changes */
  onChange?: (values: Record<string, unknown>) => void;
  /** Called when validation state changes */
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  /** Custom class name */
  className?: string;
  /** Disable all fields */
  disabled?: boolean;
  /** Show loading state */
  loading?: boolean;
  /**
   * Custom renderer components keyed by renderer key.
   * Subgraphs provide these to render domain-specific UI for CUSTOM field types.
   *
   * @example
   * ```tsx
   * const customRenderers = { 'fhir-timeline': FhirTimeline };
   * <EntityFormGenerator customRenderers={customRenderers} ... />
   * ```
   */
  customRenderers?: Record<string, React.ComponentType<CustomRendererProps>>;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Check if a schema version is compatible with the supported version.
 * Only the major version must match; minor differences are forward-compatible.
 */
function isSchemaVersionCompatible(schemaVersion: string): boolean {
  const schemaMajor = schemaVersion.split('.')[0];
  const supportedMajor = SUPPORTED_SCHEMA_VERSION.split('.')[0];
  return schemaMajor === supportedMajor;
}

/**
 * Maps backend FormFieldType enum values (SCREAMING_CASE) to widget registry keys (PascalCase).
 * The widget registry uses Mantine component names; the backend uses GraphQL enum conventions.
 */
const FIELD_TYPE_TO_WIDGET: Record<string, string> = {
  TEXT: 'TextInput',
  TEXTAREA: 'Textarea',
  NUMBER: 'NumberInput',
  CURRENCY: 'NumberInput',
  SELECT: 'Select',
  MULTISELECT: 'MultiSelect',
  BOOLEAN: 'Checkbox',
  TOGGLE: 'Switch',
  DATE: 'DateInput',
  DATETIME: 'DateTimePicker',
  TIME: 'TimeInput',
  FILE: 'FileInput',
  EMAIL: 'TextInput',
  PHONE: 'TextInput',
  SSN: 'PasswordInput',
  MRN: 'TextInput',
  NPI: 'TextInput',
  ADDRESS: 'Textarea',
  // Complex types match registry keys directly
  COMPUTED: 'COMPUTED',
  HIERARCHICAL_SELECT: 'HIERARCHICAL_SELECT',
  GRID: 'GRID',
  MASTER_DETAIL: 'MASTER_DETAIL',
};

export function EntityFormGenerator({
  subgraph,
  entityType,
  rulesetId,
  context,
  initialValues = {},
  onSubmit,
  onChange,
  onValidationChange,
  className,
  disabled = false,
  loading: externalLoading = false,
  customRenderers,
}: EntityFormGeneratorProps) {
  const [formValues, setFormValues] = useState<Record<string, unknown>>(initialValues);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Fetch context-aware schema from backend
  // DRR-162: Pass rulesetId for RS-scoped form override resolution
  const { schema, loading: schemaLoading, error } = useEntityUISchema({
    subgraph,
    entityType,
    rulesetId,
    context,
  });

  const isLoading = schemaLoading || externalLoading;

  // Initialize collapsed sections from schema defaults (collapsed: true)
  // Only run once when schema first loads
  const schemaRef = React.useRef(false);
  if (schema && !schemaRef.current) {
    schemaRef.current = true;
    const initialCollapsed = new Set<string>();
    for (const section of schema.layout.sections) {
      if (section.collapsed) initialCollapsed.add(section.id);
    }
    if (initialCollapsed.size > 0) setCollapsedSections(initialCollapsed);
  }

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  // Build compliance context for life-critical widgets
  // Map schema safetyLevel to ComplianceContext safetyLevel
  const mapSafetyLevel = (level?: string): 'ROUTINE' | 'ELEVATED' | 'CRITICAL' | undefined => {
    switch (level) {
      case 'LIFE_CRITICAL':
      case 'CRITICAL':
        return 'CRITICAL';
      case 'ELEVATED':
        return 'ELEVATED';
      case 'STANDARD':
        return 'ROUTINE';
      default:
        return undefined;
    }
  };

  const complianceContext: ComplianceContext = useMemo(
    () => ({
      isLifeCritical: schema?.safetyLevel === 'LIFE_CRITICAL' || schema?.safetyLevel === 'CRITICAL',
      safetyLevel: mapSafetyLevel(schema?.safetyLevel),
      requiresAuditLog: true,
      subgraphId: subgraph,
    }),
    [schema?.safetyLevel, subgraph]
  );

  // Handle field changes
  const handleFieldChange = useCallback(
    (fieldName: string, value: unknown) => {
      const newValues = { ...formValues, [fieldName]: value };
      setFormValues(newValues);
      setTouchedFields((prev) => new Set([...prev, fieldName]));

      // Clear field error on change
      if (errors[fieldName]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[fieldName];
          return next;
        });
      }

      onChange?.(newValues);
    },
    [formValues, errors, onChange]
  );

  // Validate form
  const validateForm = useCallback((): boolean => {
    if (!schema) return false;

    const newErrors: Record<string, string> = {};

    schema.fields.forEach((field) => {
      const value = formValues[field.name];

      // Required validation
      if (field.required && (value === undefined || value === null || value === '')) {
        newErrors[field.name] = `${field.label} is required`;
      }

      // Min/max length for strings
      if (typeof value === 'string' && field.validation) {
        if (field.validation.minLength && value.length < field.validation.minLength) {
          newErrors[field.name] = `${field.label} must be at least ${field.validation.minLength} characters`;
        }
        if (field.validation.maxLength && value.length > field.validation.maxLength) {
          newErrors[field.name] = `${field.label} must be at most ${field.validation.maxLength} characters`;
        }
      }

      // Min/max for numbers
      if (typeof value === 'number' && field.validation) {
        if (field.validation.min !== undefined && value < field.validation.min) {
          newErrors[field.name] = `${field.label} must be at least ${field.validation.min}`;
        }
        if (field.validation.max !== undefined && value > field.validation.max) {
          newErrors[field.name] = `${field.label} must be at most ${field.validation.max}`;
        }
      }
    });

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    onValidationChange?.(isValid, Object.values(newErrors));
    return isValid;
  }, [schema, formValues, onValidationChange]);

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // Touch all fields for validation display
      if (schema) {
        setTouchedFields(new Set(schema.fields.map((f) => f.name)));
      }

      if (validateForm()) {
        onSubmit?.(formValues);
      }
    },
    [schema, formValues, validateForm, onSubmit]
  );

  // Render loading state
  if (isLoading) {
    return (
      <Box className={className}>
        <Paper p="md">
          <Group>
            <Loader size="sm" />
            <Text>Loading {entityType} form...</Text>
          </Group>
        </Paper>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box className={className}>
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Form Load Error">
          <Text size="sm">{error.message}</Text>
        </Alert>
      </Box>
    );
  }

  // No schema available for this entity type (DRR-055: no fallback fields)
  if (!schema) {
    return (
      <Box className={className}>
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="Form Not Available">
          <Text size="sm">
            No form schema defined for {entityType}. Contact an architect to configure one.
          </Text>
        </Alert>
      </Box>
    );
  }

  // Schema version compatibility check
  if (!isSchemaVersionCompatible(schema.schemaVersion)) {
    return (
      <Box className={className}>
        <Alert icon={<IconAlertCircle size={16} />} color="orange" title="Schema Version Mismatch">
          <Text size="sm">
            Schema version {schema.schemaVersion} is not supported by this frontend
            (supports {SUPPORTED_SCHEMA_VERSION}). Update the frontend to render this form.
          </Text>
        </Alert>
      </Box>
    );
  }

  // Render based on layout type
  const renderForm = () => {
    switch (schema.layout.layoutType) {
      case 'stepper':
        return renderStepperForm();
      case 'tabs':
        return renderTabbedForm();
      case 'accordion':
        return renderAccordionForm();
      case 'single-page':
      default:
        return renderSinglePageForm();
    }
  };

  // Single-page layout
  const renderSinglePageForm = () => (
    <Stack gap="lg">
      {schema.layout.sections.map((section) => {
        const isCollapsible = section.collapsible === true;
        const isCollapsed = collapsedSections.has(section.id);
        const ChevronIcon = isCollapsed ? IconChevronRight : IconChevronDown;

        return (
          <Paper key={section.id} p="md" withBorder>
            {isCollapsible ? (
              <UnstyledButton onClick={() => toggleSection(section.id)} w="100%">
                <Group gap="xs" mb={isCollapsed ? 0 : 'xs'}>
                  <ChevronIcon size={16} />
                  <Text fw={600} size="lg">
                    {section.title}
                  </Text>
                </Group>
              </UnstyledButton>
            ) : (
              <Text fw={600} size="lg" mb="xs">
                {section.title}
              </Text>
            )}
            <Collapse in={!isCollapsed}>
              {section.description && (
                <Text size="sm" c="dimmed" mb="md">
                  {section.description}
                </Text>
              )}
              {section.safetyWarning && (
                <Alert icon={<IconShield size={16} />} color="yellow" mb="md">
                  {section.safetyWarning}
                </Alert>
              )}
              <Grid gutter="sm">
                {section.fields.map((fieldName) => {
                  const field = schema.fields.find((f) => f.name === fieldName);
                  if (!field) return null;
                  const span = field.width === 'half' ? 6 : 12;
                  return (
                    <Grid.Col key={field.name} span={span}>
                      <EntityFormField
                        field={field}
                        value={formValues[field.name]}
                        onChange={(value) => handleFieldChange(field.name, value)}
                        error={touchedFields.has(field.name) ? errors[field.name] : undefined}
                        disabled={disabled}
                        complianceContext={complianceContext}
                        formValues={formValues}
                        customRenderers={customRenderers}
                      />
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );

  // Stepper layout (wizard)
  const renderStepperForm = () => (
    <Stepper active={activeStep} onStepClick={setActiveStep}>
      {schema.layout.sections.map((section, index) => (
        <Stepper.Step key={section.id} label={section.title} description={section.description}>
          <Stack gap="sm" mt="md">
            {section.fields.map((fieldName) => {
              const field = schema.fields.find((f) => f.name === fieldName);
              if (!field) return null;
              return (
                <EntityFormField
                  key={field.name}
                  field={field}
                  value={formValues[field.name]}
                  onChange={(value) => handleFieldChange(field.name, value)}
                  error={touchedFields.has(field.name) ? errors[field.name] : undefined}
                  disabled={disabled}
                  complianceContext={complianceContext}
                  formValues={formValues}
                  customRenderers={customRenderers}
                />
              );
            })}
          </Stack>
          <Group justify="flex-end" mt="xl">
            {index > 0 && (
              <Button variant="default" onClick={() => setActiveStep(index - 1)}>
                Back
              </Button>
            )}
            {index < schema.layout.sections.length - 1 ? (
              <Button onClick={() => setActiveStep(index + 1)}>Next</Button>
            ) : (
              <Button type="submit" onClick={handleSubmit}>
                {schema.layout.submitLabel || 'Submit'}
              </Button>
            )}
          </Group>
        </Stepper.Step>
      ))}
    </Stepper>
  );

  // Tabbed layout
  const renderTabbedForm = () => (
    <Tabs defaultValue={schema.layout.sections[0]?.id}>
      <Tabs.List>
        {schema.layout.sections.map((section) => (
          <Tabs.Tab key={section.id} value={section.id}>
            {section.title}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      {schema.layout.sections.map((section) => (
        <Tabs.Panel key={section.id} value={section.id} pt="md">
          <Stack gap="sm">
            {section.fields.map((fieldName) => {
              const field = schema.fields.find((f) => f.name === fieldName);
              if (!field) return null;
              return (
                <EntityFormField
                  key={field.name}
                  field={field}
                  value={formValues[field.name]}
                  onChange={(value) => handleFieldChange(field.name, value)}
                  error={touchedFields.has(field.name) ? errors[field.name] : undefined}
                  disabled={disabled}
                  complianceContext={complianceContext}
                  formValues={formValues}
                  customRenderers={customRenderers}
                />
              );
            })}
          </Stack>
        </Tabs.Panel>
      ))}
    </Tabs>
  );

  // Accordion layout - fallback to single-page for now
  const renderAccordionForm = () => renderSinglePageForm();

  return (
    <Box className={className}>
      {/* Safety level indicator */}
      {complianceContext.isLifeCritical && (
        <Alert icon={<IconShield size={16} />} color="red" mb="md">
          <Text size="sm" fw={500}>
            Life-Critical Form - All changes are audited
          </Text>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {renderForm()}

        {/* Submit button for non-stepper layouts */}
        {schema.layout.layoutType !== 'stepper' && (
          <Group justify="flex-end" mt="xl">
            {schema.layout.cancelLabel && (
              <Button variant="default" type="button">
                {schema.layout.cancelLabel}
              </Button>
            )}
            <Button type="submit" disabled={disabled}>
              {schema.layout.submitLabel || 'Submit'}
            </Button>
          </Group>
        )}
      </form>
    </Box>
  );
}

// =============================================================================
// FIELD RENDERER
// =============================================================================

interface EntityFormFieldProps {
  field: {
    name: string;
    path: string;
    fieldType: string;
    label: string;
    required: boolean;
    placeholder?: string;
    helpText?: string;
    options?: Array<{ value: string; label: string }>;
    validation?: {
      minLength?: number;
      maxLength?: number;
      min?: number;
      max?: number;
      pattern?: string;
    };
    componentProps?: Record<string, unknown>;
    // DRR-060: Type-specific configuration for complex field types
    computedConfig?: Record<string, unknown>;
    hierarchicalConfig?: Record<string, unknown>;
    gridConfig?: Record<string, unknown>;
    masterDetailConfig?: Record<string, unknown>;
    // Reference config for REFERENCE field type (H4: cross-subgraph entity lookup)
    referenceConfig?: {
      subgraph: string;
      entityType: string;
      query?: string;
      valuePath?: string;
      labelPath?: string;
      searchFields?: string[];
      allowCreate?: boolean;
    };
    // Custom renderer support
    rendererKey?: string;
    customRendererProps?: string;
  };
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  complianceContext?: ComplianceContext;
  /** DRR-060: All current form values for cross-field references (COMPUTED, MASTER_DETAIL) */
  formValues?: Record<string, unknown>;
  /** Custom renderer components keyed by renderer key */
  customRenderers?: Record<string, React.ComponentType<CustomRendererProps>>;
}

function EntityFormField({
  field,
  value,
  onChange,
  error,
  disabled = false,
  complianceContext,
  formValues,
  customRenderers,
}: EntityFormFieldProps) {
  // Handle CUSTOM field type — delegate to subgraph-provided renderer
  if (field.fieldType === 'CUSTOM' && field.rendererKey) {
    const CustomComponent = customRenderers?.[field.rendererKey];
    if (!CustomComponent) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          <Text size="sm">
            Custom renderer &apos;{field.rendererKey}&apos; not provided for field &apos;{field.name}&apos;
          </Text>
        </Alert>
      );
    }
    return (
      <Suspense
        fallback={
          <Box>
            <Text size="sm" c="dimmed" mb="xs">
              {field.label}
            </Text>
            <Loader size="sm" />
          </Box>
        }
      >
        <CustomComponent
          field={field as unknown as UISchemaField}
          value={value}
          onChange={onChange}
          formValues={formValues ?? {}}
          complianceContext={complianceContext}
          disabled={disabled}
        />
      </Suspense>
    );
  }

  // Handle REFERENCE field type — entity search autocomplete (H4: cross-subgraph lookup)
  if (field.fieldType === 'REFERENCE' && field.referenceConfig) {
    return (
      <EntitySearchField
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
      />
    );
  }

  // Transform backend options to Mantine combobox format.
  // Backend returns { value, label, group, disabled, description } where group can be null.
  // Mantine's parseItem checks 'group' in item — null group triggers grouped-item path and crashes.
  // Strip to { value, label, disabled?, group? } with null group removed.
  const selectData = field.options?.map((opt: { value: string; label: string; group?: string | null; disabled?: boolean | null }) => {
    const item: { value: string; label: string; disabled?: boolean } = { value: opt.value, label: opt.label };
    if (opt.disabled) item.disabled = true;
    return item;
  });

  // Build widget props
  // MultiSelect requires an array value — default '' causes _value.map crash
  const isMultiSelect = (FIELD_TYPE_TO_WIDGET[field.fieldType] ?? field.fieldType) === 'MultiSelect';
  const safeValue = isMultiSelect
    ? (Array.isArray(value) ? value : [])
    : (value ?? '');
  const widgetProps: BaseWidgetProps = {
    name: field.name,
    label: field.label,
    value: safeValue,
    onChange,
    required: field.required,
    disabled,
    placeholder: field.placeholder,
    description: field.helpText,
    error,
    componentProps: {
      data: selectData,
      // DRR-060: Pass type-specific configs for complex field types
      computedConfig: field.computedConfig,
      hierarchicalConfig: field.hierarchicalConfig,
      gridConfig: field.gridConfig,
      masterDetailConfig: field.masterDetailConfig,
      ...field.componentProps,
    },
    complianceContext,
  };

  // Map backend FormFieldType enum (SCREAMING_CASE) to widget registry keys (PascalCase)
  const widgetType = FIELD_TYPE_TO_WIDGET[field.fieldType] ?? field.fieldType;

  if (!hasWidgetComponent(widgetType)) {
    // Fallback to TextInput for unknown types
    console.warn(`Unknown widget type: ${widgetType}, falling back to TextInput`);
    const TextInputComponent = getWidgetComponent('TextInput');
    if (TextInputComponent) {
      return <TextInputComponent {...widgetProps} />;
    }
    return <Text c="red">Widget not found: {widgetType}</Text>;
  }

  // Safe non-null assertion: hasWidgetComponent(widgetType) returned true above
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const WidgetComponent = getWidgetComponent(widgetType)!;

  // Wrap all components in Suspense - works for both lazy and eager components
  // Suspense only shows fallback for lazy components that are loading
  return (
    <Suspense
      fallback={
        <Box>
          <Text size="sm" c="dimmed" mb="xs">
            {field.label}
          </Text>
          <Loader size="sm" />
        </Box>
      }
    >
      <WidgetComponent {...widgetProps} />
    </Suspense>
  );
}

export default EntityFormGenerator;
